import Log from 'src/lib/loggingUtil'
import {
  BrazeContentCard,
  BrazeContentCardComponent,
  BrazeContentCardLocation,
  BrazeContentCardLocationRange,
  BrazeContentCardScreen,
  TypedBrazeContentCard,
} from 'src/lib/braze/braze.types'

const isValidBrazeContentCardScreen = (
  looselyTypedScreen: string,
): looselyTypedScreen is BrazeContentCardScreen =>
  Object.values(BrazeContentCardScreen).includes(looselyTypedScreen as BrazeContentCardScreen)

const isValidBrazeContentCardLocation = (
  looselyTypedLocation: number,
): looselyTypedLocation is BrazeContentCardLocation =>
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  BrazeContentCardLocationRange.includes(looselyTypedLocation as BrazeContentCardLocation)

const isValidBrazeContentCardComponent = (
  looselyTypedComponentName: string,
): looselyTypedComponentName is BrazeContentCardComponent =>
  Object.values(BrazeContentCardComponent).includes(
    looselyTypedComponentName as BrazeContentCardComponent,
  )

/**
 * Filters out any Braze Content Cards that do no have our required schema (`screen`,`location`)
 * and returns a type safe array of `BrazeContentCard` objects.
 */
const getValidBrazeContentCards = (
  looselyTypedContentCards: TypedBrazeContentCard[],
): BrazeContentCard[] =>
  looselyTypedContentCards.reduce<BrazeContentCard[]>((previousValue, currentValue) => {
    /* The default `locationId` value (`??`) does not represent a default
     * `brazeContentCardLocationRange` value. It's simply to protect against `Number`
     * returning `NaN` */
    const locationId = Number(currentValue.extras?.location) || 0
    const screen = currentValue.extras?.screen ?? ''
    const component =
      currentValue.extras?.component &&
      isValidBrazeContentCardComponent(currentValue?.extras?.component)
        ? currentValue.extras?.component
        : undefined

    if (isValidBrazeContentCardScreen(screen) && isValidBrazeContentCardLocation(locationId)) {
      previousValue.push({
        ...currentValue,
        extras: {
          ...currentValue.extras,
          location: locationId,
          screen: screen,
          component: component,
          order: Number(currentValue.extras?.order) || undefined,
          tags: currentValue.extras.tags?.split(','),
        },
      })
    } else {
      Log.error(`Invalid Braze Content Card schema detected: ${JSON.stringify(currentValue)}`)
    }

    return previousValue
  }, [])

export {getValidBrazeContentCards}
